import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Download, X, Loader2, Users, FileText, UploadCloud, Mail, Phone, Package, Eye, EyeOff, Type, Save, LayoutGrid, Shield, User } from 'lucide-react';
import { useSiteContent, CONTENT_FIELDS } from '../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'live', label: 'Featured / Live' },
  { value: 'upcoming', label: 'Upcoming Auction' },
  { value: 'past', label: 'Previous Result' },
];

const CATEGORY_OPTIONS = [
  'Apollo Program', 'Gemini Program', 'Mercury Program', 'Skylab Program', 'Astronaut Personal Effects',
  'Astronaut Autographs', 'Mission Control Equipment', 'Flight Hardware', 'Flown Artifacts',
  'Rare Documents', 'Space Shuttle Era', 'Soviet Space Program', 'Meteorites & Lunar Samples',
];

const blankArtifact = (status = 'past') => ({
  title: '', category: 'Apollo Program', year: '', description: '',
  estimated_value: '', image: '', status, auction_date: '',
  current_bid: 0, starting_bid: 0, bids_count: 0, hidden: false,
});

const PreviewCard = ({ art }) => {
  let footerLabel = 'Estimate';
  let footerValue = 'Estimate on request';
  let badge = 'Preview Lot';
  if (art.status === 'live') { footerLabel = 'Estimate'; footerValue = 'Estimate on request'; badge = 'Current Auction'; }
  else if (art.status === 'past') { footerLabel = 'Hammer Price'; footerValue = art.estimated_value || '—'; badge = 'Sold'; }
  else if (art.status === 'record_sale') { footerLabel = 'Hammer Price'; footerValue = art.estimated_value || '—'; badge = 'Sold'; }
  else if (art.status === 'upcoming') { footerLabel = 'Estimate'; footerValue = 'Estimate on request'; badge = 'Preview Lot'; }

  return (
    <div className="mb-8" data-testid="admin-card-preview">
      <span className="text-[9px] uppercase tracking-[0.2em] text-luxury-muted font-sans-luxury block mb-2">Live Preview — how this lot appears on the website</span>
      <div className="bg-luxury-black border border-white/10 overflow-hidden max-w-sm mx-auto">
        <div className="relative aspect-[4/3] bg-black overflow-hidden">
          {art.image ? (
            <img src={art.image} alt="preview" onError={(e) => { e.currentTarget.style.opacity = '0.15'; }} className="w-full h-full object-cover grayscale brightness-90" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-luxury-muted text-[10px] font-sans-luxury uppercase tracking-widest">No photo yet</div>
          )}
          <div className="absolute top-3 left-3 border border-luxury-gold/60 bg-black/70 text-luxury-gold px-2.5 py-1 text-[8px] font-sans-luxury font-semibold uppercase tracking-[0.2em]">{badge}</div>
        </div>
        <div className="p-5">
          <span className="text-[9px] uppercase tracking-[0.2em] text-luxury-gold font-semibold">{(art.category || 'Category')}{art.year ? ` · ${art.year}` : ''}</span>
          <h4 className="text-base font-serif-luxury text-white mt-1.5 mb-2">{art.title || 'Untitled Lot'}</h4>
          <p className="text-[11px] text-luxury-muted leading-relaxed line-clamp-2 font-sans-luxury">{art.description || 'A description of the lot will appear here.'}</p>
          <div className="border-t border-white/5 pt-4 mt-4 flex justify-between items-end">
            <div>
              <span className="text-[8px] uppercase tracking-[0.2em] text-luxury-muted block">{footerLabel}</span>
              <span className="text-sm font-mono-luxury text-luxury-gold font-semibold">{footerValue}</span>
            </div>
            {art.status === 'live' ? (
              <span className="border border-luxury-gold text-luxury-gold text-[8px] uppercase tracking-[0.2em] font-bold px-4 py-2">Inquire</span>
            ) : (
              <span className="text-[9px] bg-white/5 border border-white/10 text-luxury-goldLight font-sans-luxury uppercase px-2.5 py-1 tracking-[0.15em]">{art.auction_date || 'Date'}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminPanel = () => {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('past');
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [signups, setSignups] = useState({ total: 0, list: [] });
  const [consignments, setConsignments] = useState({ total: 0, list: [] });
  const [exporting, setExporting] = useState(false);
  const [exportingConsign, setExportingConsign] = useState(false);
  const [section, setSection] = useState('sitetext');

  const fetchArtifacts = async () => {
    try {
      const res = await axios.get(`${API}/artifacts?include_hidden=true`);
      setArtifacts(res.data);
    } catch (err) {
      toast.error('Load Error', { description: 'Unable to load artifacts.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSignups = async () => {
    try {
      const res = await axios.get(`${API}/signups`);
      setSignups({ total: res.data.total, list: res.data.signups || [] });
    } catch (err) {
      // non-blocking
    }
  };

  const fetchConsignments = async () => {
    try {
      const res = await axios.get(`${API}/admin/consignments`);
      setConsignments({ total: res.data.total, list: res.data.consignments || [] });
    } catch (err) {
      // non-blocking
    }
  };

  useEffect(() => {
    fetchArtifacts();
    fetchSignups();
    fetchConsignments();
  }, []);

  const openNew = () => {
    setEditing(blankArtifact(statusFilter));
    setIsNew(true);
  };

  const openEdit = (art) => {
    setEditing({ ...art });
    setIsNew(false);
  };

  const handleField = (key, value) => {
    setEditing((prev) => ({ ...prev, [key]: value }));
  };

  const uploadImage = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid file', { description: 'Please choose an image (JPG, PNG, GIF, or WEBP).' });
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/upload-image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      handleField('image', fullUrl);
      toast.success('Photo Uploaded', { description: 'The auction photo is attached to this lot.' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Unable to upload the photo.';
      toast.error('Upload Failed', { description: msg });
    } finally {
      setUploading(false);
    }
  };

  const saveArtifact = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.title.trim()) {
      toast.error('Title Required', { description: 'Please enter a title for this item.' });
      return;
    }
    if (!editing.image) {
      toast.error('Photo Required', { description: 'Please upload a photo for this item.' });
      return;
    }
    setSaving(true);
    const payload = {
      title: editing.title,
      category: editing.category,
      year: editing.year,
      description: editing.description,
      estimated_value: editing.estimated_value,
      image: editing.image,
      status: editing.status,
      auction_date: editing.auction_date,
      current_bid: parseFloat(editing.current_bid) || 0,
      starting_bid: parseFloat(editing.starting_bid) || 0,
      bids_count: parseInt(editing.bids_count, 10) || 0,
      hidden: !!editing.hidden,
    };
    try {
      if (isNew) {
        await axios.post(`${API}/admin/artifacts`, payload);
        toast.success('Lot Added', { description: `"${payload.title}" is now live in the catalog.` });
      } else {
        await axios.patch(`${API}/admin/artifacts/${editing.id}`, payload);
        toast.success('Lot Updated', { description: `"${payload.title}" has been updated.` });
      }
      setEditing(null);
      setLoading(true);
      await fetchArtifacts();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Unable to save the lot.';
      toast.error('Save Failed', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  const deleteArtifact = async (art) => {
    if (!window.confirm(`Delete "${art.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/artifacts/${art.id}`);
      setArtifacts((prev) => prev.filter((a) => a.id !== art.id));
      toast.info('Lot Removed', { description: `"${art.title}" deleted from catalog.` });
    } catch (err) {
      toast.error('Delete Failed', { description: 'Unable to delete the lot.' });
    }
  };

  const toggleHidden = async (art) => {
    const nextHidden = !art.hidden;
    try {
      await axios.patch(`${API}/admin/artifacts/${art.id}`, { hidden: nextHidden });
      setArtifacts((prev) => prev.map((a) => (a.id === art.id ? { ...a, hidden: nextHidden } : a)));
      toast.success(nextHidden ? 'Item Hidden' : 'Item Visible', {
        description: nextHidden
          ? `"${art.title}" is now hidden from the public site.`
          : `"${art.title}" is now visible on the public site.`,
      });
    } catch (err) {
      toast.error('Update Failed', { description: 'Unable to change visibility.' });
    }
  };

  const deleteSignup = async (s) => {
    if (!window.confirm(`Delete signup "${s.email}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/signups/${s.id}`);
      setSignups((prev) => ({ total: Math.max(0, prev.total - 1), list: prev.list.filter((x) => x.id !== s.id) }));
      toast.info('Signup Removed', { description: `"${s.email}" deleted.` });
    } catch (err) {
      toast.error('Delete Failed', { description: 'Unable to delete signup.' });
    }
  };

  const deleteConsignment = async (c) => {
    if (!window.confirm(`Delete consignment from "${c.name || c.email}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/consignments/${c.id}`);
      setConsignments((prev) => ({ total: Math.max(0, prev.total - 1), list: prev.list.filter((x) => x.id !== c.id) }));
      toast.info('Consignment Removed', { description: `Inquiry from "${c.name || c.email}" deleted.` });
    } catch (err) {
      toast.error('Delete Failed', { description: 'Unable to delete consignment.' });
    }
  };

  const exportSignups = async () => {    setExporting(true);
    try {
      const res = await axios.get(`${API}/signups/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lunar_legacies_signups.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Ready', { description: 'Signups CSV downloaded.' });
    } catch (err) {
      toast.error('Export Failed', { description: 'Unable to export signups.' });
    } finally {
      setExporting(false);
    }
  };

  const exportConsignments = async () => {
    setExportingConsign(true);
    try {
      const res = await axios.get(`${API}/admin/consignments/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lunar_legacies_consignments.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Ready', { description: 'Consignments CSV downloaded.' });
    } catch (err) {
      toast.error('Export Failed', { description: 'Unable to export consignments.' });
    } finally {
      setExportingConsign(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  // --- Site Text (editable homepage copy) ---
  const { content: siteContent, refresh: refreshContent } = useSiteContent();
  const [siteText, setSiteText] = useState(siteContent);
  const [savingText, setSavingText] = useState(false);
  useEffect(() => { setSiteText(siteContent); }, [siteContent]);

  const saveSiteText = async () => {
    setSavingText(true);
    try {
      await axios.put(`${API}/admin/content`, { content: siteText });
      await refreshContent();
      toast.success('Website Text Saved', { description: 'Your homepage wording is updated and live.' });
    } catch (err) {
      toast.error('Save Failed', { description: 'Unable to save website text.' });
    } finally {
      setSavingText(false);
    }
  };

  const addCustomBlock = () =>
    setSiteText((prev) => ({ ...prev, custom_blocks: [...(prev.custom_blocks || []), { id: `blk_${Date.now()}`, title: '', body: '' }] }));
  const updateCustomBlock = (id, field, value) =>
    setSiteText((prev) => ({ ...prev, custom_blocks: (prev.custom_blocks || []).map((b) => (b.id === id ? { ...b, [field]: value } : b)) }));
  const removeCustomBlock = (id) =>
    setSiteText((prev) => ({ ...prev, custom_blocks: (prev.custom_blocks || []).filter((b) => b.id !== id) }));

  const contentGroups = CONTENT_FIELDS.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});

  const filtered = artifacts.filter((a) => a.status === statusFilter);

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Admin section navigation — always visible, one clear button per area */}
      <div data-testid="admin-control-room">
        <span className="text-[10px] uppercase tracking-[0.3em] text-luxury-gold font-bold flex items-center gap-2 mb-3">
          <Shield size={14} /> Admin Control Room
        </span>
        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-5" data-testid="admin-section-nav">
          {[
            { id: 'sitetext', label: 'Website Text', icon: Type },
            { id: 'items', label: 'Items', icon: LayoutGrid },
            { id: 'consignments', label: 'Consignments', icon: FileText },
            { id: 'mailing', label: 'Mailing List', icon: Mail },
          ].map((t) => {
            const Icon = t.icon;
            const active = section === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSection(t.id)}
                className={`flex items-center gap-2 px-5 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold font-sans-luxury border transition-all duration-300 ${active ? 'border-luxury-gold text-luxury-gold bg-luxury-gold/10' : 'border-white/10 text-luxury-muted hover:text-white hover:border-white/40'}`}
                data-testid={`admin-nav-${t.id}`}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Site Text Editor */}
      <div className={`border border-luxury-gold/20 bg-luxury-charcoal p-6 md:p-8 ${section === 'sitetext' ? '' : 'hidden'}`} data-testid="admin-sitetext-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-luxury-gold font-semibold mb-2 flex items-center gap-2">
              <Type size={13} /> Website Text
            </span>
            <p className="text-[11px] text-luxury-muted font-sans-luxury max-w-xl">Edit the homepage wording without code. Changes go live as soon as you save.</p>
          </div>
          <button
            onClick={saveSiteText}
            disabled={savingText}
            className="btn-gold font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold px-6 py-3.5 rounded-none flex items-center justify-center gap-2 self-start sm:self-center disabled:opacity-40"
            data-testid="admin-save-sitetext"
          >
            {savingText ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save Website Text
          </button>
        </div>

        <div className="space-y-6">
          {Object.entries(contentGroups).map(([group, fields]) => (
            <div key={group} className="border border-white/5 bg-black/30 p-4">
              <h4 className="text-[10px] uppercase tracking-[0.25em] text-luxury-goldLight font-semibold mb-4">{group}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.map((f) => (
                  <div key={f.key} className={f.type === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="block text-[9px] uppercase tracking-widest text-luxury-muted font-sans-luxury mb-1.5">{f.label}</label>
                    {f.type === 'textarea' ? (
                      <textarea
                        rows={3}
                        value={siteText[f.key] || ''}
                        onChange={(e) => setSiteText((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full bg-black border border-white/10 text-white focus:border-luxury-gold text-xs px-3 py-2.5 outline-none rounded-none resize-none transition-colors duration-300 leading-relaxed"
                        data-testid={`admin-sitetext-${f.key}`}
                      />
                    ) : f.type === 'colortext' ? (
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          value={siteText[f.key] || ''}
                          placeholder="#FFFFFF"
                          onChange={(e) => setSiteText((prev) => ({ ...prev, [f.key]: e.target.value }))}
                          className="flex-1 bg-black border border-white/10 text-white focus:border-luxury-gold text-xs px-3 py-2.5 outline-none rounded-none transition-colors duration-300 font-mono"
                          data-testid={`admin-sitetext-${f.key}`}
                        />
                        <span
                          className="h-9 w-9 border border-white/20 shrink-0"
                          style={{ backgroundColor: siteText[f.key] || '#FFFFFF' }}
                          title="Color preview"
                          data-testid={`admin-sitetext-${f.key}-swatch`}
                        />
                      </div>
                    ) : f.type === 'select' ? (
                      <select
                        value={siteText[f.key] || (f.options && f.options[0] && f.options[0].value) || ''}
                        onChange={(e) => setSiteText((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full bg-black border border-white/10 text-white focus:border-luxury-gold text-xs px-3 py-2.5 outline-none rounded-none transition-colors duration-300"
                        data-testid={`admin-sitetext-${f.key}`}
                      >
                        {(f.options || []).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={siteText[f.key] || ''}
                        onChange={(e) => setSiteText((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full bg-black border border-white/10 text-white focus:border-luxury-gold text-xs px-3 py-2.5 outline-none rounded-none transition-colors duration-300"
                        data-testid={`admin-sitetext-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Custom homepage sections */}
        <div className="border border-white/5 bg-black/30 p-4 mt-6" data-testid="admin-custom-blocks">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.25em] text-luxury-goldLight font-semibold">Custom Homepage Sections</h4>
              <p className="text-[10px] text-luxury-muted font-sans-luxury mt-1">Add your own titled paragraphs. They appear on the homepage after the About section.</p>
            </div>
            <button
              onClick={addCustomBlock}
              className="border border-luxury-gold/40 text-luxury-gold hover:bg-luxury-gold hover:text-black text-[10px] uppercase tracking-widest px-3 py-2 flex items-center gap-1.5 transition-colors duration-300 flex-shrink-0"
              data-testid="admin-add-block"
            >
              <Plus size={12} /> Add Section
            </button>
          </div>
          {(siteText.custom_blocks || []).length === 0 ? (
            <p className="text-[11px] text-luxury-muted font-sans-luxury border border-white/5 p-3">No custom sections yet.</p>
          ) : (
            <div className="space-y-3">
              {(siteText.custom_blocks || []).map((b, i) => (
                <div key={b.id} className="border border-white/10 p-3 relative" data-testid={`admin-block-${i}`}>
                  <button onClick={() => removeCustomBlock(b.id)} className="absolute top-2 right-2 text-luxury-muted hover:text-red-400 transition-colors duration-300" title="Remove section" data-testid={`admin-block-delete-${i}`}>
                    <Trash2 size={13} />
                  </button>
                  <label className="block text-[9px] uppercase tracking-widest text-luxury-muted font-sans-luxury mb-1.5">Section Title</label>
                  <input
                    type="text"
                    value={b.title || ''}
                    onChange={(e) => updateCustomBlock(b.id, 'title', e.target.value)}
                    className="w-full bg-black border border-white/10 text-white focus:border-luxury-gold text-xs px-3 py-2.5 outline-none rounded-none transition-colors duration-300 mb-3"
                    data-testid={`admin-block-title-${i}`}
                  />
                  <label className="block text-[9px] uppercase tracking-widest text-luxury-muted font-sans-luxury mb-1.5">Section Text</label>
                  <textarea
                    rows={3}
                    value={b.body || ''}
                    onChange={(e) => updateCustomBlock(b.id, 'body', e.target.value)}
                    className="w-full bg-black border border-white/10 text-white focus:border-luxury-gold text-xs px-3 py-2.5 outline-none rounded-none resize-none transition-colors duration-300 leading-relaxed"
                    data-testid={`admin-block-body-${i}`}
                  />
                </div>
              ))}
            </div>
          )}
          <p className="text-[9px] text-luxury-muted/70 mt-3 font-sans-luxury">After adding or editing sections, click "Save Website Text" above to publish.</p>
        </div>
      </div>

      {/* Mailing List Signups */}
      <div className={`border border-luxury-gold/20 bg-luxury-charcoal p-6 md:p-8 ${section === 'mailing' ? '' : 'hidden'}`} data-testid="admin-signups-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-luxury-gold font-semibold block mb-2 flex items-center gap-2">
              <Mail size={13} /> Mailing List Signups
            </span>
            <div className="flex items-center gap-2">
              <Users size={16} className="text-luxury-gold" />
              <span className="text-2xl font-mono-luxury font-bold text-white">{signups.total}</span>
              <span className="text-[10px] text-luxury-muted uppercase tracking-widest">Total</span>
            </div>
          </div>
          <button
            onClick={exportSignups}
            disabled={exporting || signups.total === 0}
            className="btn-gold font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold px-6 py-3.5 rounded-none flex items-center justify-center gap-2 self-start sm:self-center disabled:opacity-40"
            data-testid="admin-export-signups"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Export Signups CSV
          </button>
        </div>

        {signups.list.length === 0 ? (
          <p className="text-[11px] text-luxury-muted font-sans-luxury border border-white/5 bg-black/30 p-4">No signups yet.</p>
        ) : (
          <div className="border border-white/5 bg-black/30 max-h-64 overflow-y-auto" data-testid="admin-signups-list">
            <table className="w-full text-left text-[11px] font-sans-luxury">
              <thead className="text-luxury-muted uppercase tracking-widest text-[9px] sticky top-0 bg-luxury-charcoal">
                <tr>
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {signups.list.map((s, i) => (
                  <tr key={s.id || i} className="border-t border-white/5 text-white/90" data-testid={`admin-signup-row-${i}`}>
                    <td className="px-4 py-2.5">{s.name || '—'}</td>
                    <td className="px-4 py-2.5 text-luxury-goldLight"><a href={s.email ? `mailto:${s.email}` : undefined} className="hover:text-luxury-gold transition-colors">{s.email}</a></td>
                    <td className="px-4 py-2.5 text-luxury-muted">{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => deleteSignup(s)} className="p-1.5 border border-white/10 text-luxury-muted hover:text-red-400 hover:border-red-400/40 transition-colors duration-300" title="Delete signup" data-testid={`admin-delete-signup-${i}`}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Consignment Inquiries */}
      <div className={`border border-luxury-gold/20 bg-luxury-charcoal p-6 md:p-8 ${section === 'consignments' ? '' : 'hidden'}`} data-testid="admin-consignments-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-luxury-gold font-semibold block mb-2 flex items-center gap-2">
              <Package size={13} /> Consignment Inquiries
            </span>
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-luxury-gold" />
              <span className="text-2xl font-mono-luxury font-bold text-white">{consignments.total}</span>
              <span className="text-[10px] text-luxury-muted uppercase tracking-widest">Total</span>
            </div>
          </div>
          <button
            onClick={exportConsignments}
            disabled={exportingConsign || consignments.total === 0}
            className="btn-gold font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold px-6 py-3.5 rounded-none flex items-center justify-center gap-2 self-start sm:self-center disabled:opacity-40"
            data-testid="admin-export-consignments"
          >
            {exportingConsign ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Export Consignments CSV
          </button>
        </div>

        {consignments.list.length === 0 ? (
          <p className="text-[11px] text-luxury-muted font-sans-luxury border border-white/5 bg-black/30 p-4">No consignment inquiries yet.</p>
        ) : (
          <div className="space-y-3 max-h-[32rem] overflow-y-auto" data-testid="admin-consignments-list">
            {consignments.list.map((c, i) => (
              <div key={c.id || i} className="border border-white/5 bg-black/30 p-4" data-testid={`admin-consignment-row-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-serif-luxury text-white">{c.name || '—'}</span>
                    <span className="text-[9px] uppercase tracking-widest text-luxury-gold border border-luxury-gold/30 px-2 py-0.5">{c.status || 'New Inquiry'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-luxury-muted font-sans-luxury">{fmtDate(c.created_at)}</span>
                    <button onClick={() => deleteConsignment(c)} className="p-1.5 border border-white/10 text-luxury-muted hover:text-red-400 hover:border-red-400/40 transition-colors duration-300" title="Delete consignment" data-testid={`admin-delete-consignment-${i}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-luxury-muted leading-relaxed font-sans-luxury whitespace-pre-wrap">{c.item_description || '—'}</p>
                <div className="mt-3 border-t border-white/10 pt-3" data-testid={`admin-consignment-contact-${i}`}>
                  <span className="block text-[9px] uppercase tracking-[0.25em] text-luxury-gold font-semibold mb-2">Contact Information</span>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-[11px] font-sans-luxury">
                    <div>
                      <span className="block text-[8px] uppercase tracking-widest text-luxury-muted mb-0.5">Name</span>
                      <span className="text-white flex items-center gap-1.5"><User size={11} className="text-luxury-gold" /> {c.name || '—'}</span>
                    </div>
                    <div>
                      <span className="block text-[8px] uppercase tracking-widest text-luxury-muted mb-0.5">Email</span>
                      <a href={c.email ? `mailto:${c.email}` : undefined} className="text-luxury-goldLight flex items-center gap-1.5 break-all"><Mail size={11} /> {c.email || '—'}</a>
                    </div>
                    <div>
                      <span className="block text-[8px] uppercase tracking-widest text-luxury-muted mb-0.5">Phone</span>
                      <span className="text-white/80 flex items-center gap-1.5"><Phone size={11} className="text-luxury-gold" /> {c.phone || '—'}</span>
                    </div>
                  </div>
                </div>
                {(c.photos && c.photos.length > 0) && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {c.photos.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noreferrer" className="block w-16 h-16 border border-white/10 overflow-hidden bg-black hover:border-luxury-gold transition-colors">
                        <img src={url} alt={`Photo ${idx + 1}`} onError={(e) => { e.currentTarget.style.opacity = '0.2'; }} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Catalog management */}
      <div className={`border border-white/5 bg-luxury-charcoal p-6 md:p-8 ${section === 'items' ? '' : 'hidden'}`} data-testid="admin-items-section">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-luxury-gold font-semibold block mb-1">Auction Catalog Editor</span>
            <h3 className="text-lg font-serif-luxury text-white">Manage Lots, Photos, Prices & Dates</h3>
          </div>
          <button
            onClick={openNew}
            className="btn-gold font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold px-6 py-3.5 rounded-none flex items-center gap-2 self-start"
            data-testid="admin-add-lot"
          >
            <Plus size={14} /> Add New Lot
          </button>
        </div>

        {/* Status filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-status-filter">
          {STATUS_OPTIONS.map((opt) => {
            const count = artifacts.filter((a) => a.status === opt.value).length;
            return (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`text-[10px] uppercase tracking-[0.15em] font-sans-luxury font-semibold px-4 py-2.5 border transition-all duration-300 ${statusFilter === opt.value ? 'border-luxury-gold text-luxury-gold bg-luxury-gold/5' : 'border-white/10 text-luxury-muted hover:border-white/30'}`}
                data-testid={`admin-filter-${opt.value}`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-12 text-luxury-gold font-serif-luxury animate-pulse tracking-widest">LOADING CATALOG...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-luxury-muted font-sans-luxury text-xs border border-white/5 bg-black/30">
            No lots in this category yet. Use “Add New Lot” to create one.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((art) => (
              <div key={art.id} className={`flex items-center gap-4 border bg-black/30 p-3 hover:border-luxury-gold/30 transition-colors duration-300 ${art.hidden ? 'border-white/5 opacity-60' : 'border-white/5'}`} data-testid={`admin-lot-${art.id}`}>
                <div className="w-16 h-16 flex-shrink-0 bg-black overflow-hidden border border-white/10">
                  {art.image ? <img src={art.image} alt={art.title} className="w-full h-full object-cover grayscale brightness-75" /> : null}
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-widest text-luxury-gold font-semibold">{art.category} · {art.year}</span>
                    {art.hidden && (
                      <span className="text-[8px] uppercase tracking-widest text-luxury-muted border border-white/15 px-1.5 py-0.5 flex items-center gap-1" data-testid={`admin-hidden-badge-${art.id}`}>
                        <EyeOff size={9} /> Hidden
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm font-serif-luxury text-white truncate">{art.title}</h4>
                  <div className="text-[10px] text-luxury-muted font-mono-luxury mt-0.5">
                    {art.estimated_value} · {art.auction_date}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => toggleHidden(art)} className={`p-2.5 border border-white/10 transition-colors duration-300 ${art.hidden ? 'text-luxury-muted hover:text-luxury-gold hover:border-luxury-gold' : 'text-luxury-gold hover:border-luxury-gold'}`} title={art.hidden ? 'Show on public site' : 'Hide from public site'} data-testid={`admin-toggle-hidden-${art.id}`}>
                    {art.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button onClick={() => openEdit(art)} className="p-2.5 border border-white/10 text-luxury-gold hover:border-luxury-gold transition-colors duration-300" title="Edit" data-testid={`admin-edit-${art.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => deleteArtifact(art)} className="p-2.5 border border-white/10 text-luxury-muted hover:text-red-400 hover:border-red-400/40 transition-colors duration-300" title="Delete" data-testid={`admin-delete-${art.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit / Create Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-md px-4 py-10 overflow-y-auto" data-testid="admin-editor-modal">
          <div className="bg-luxury-charcoal border border-luxury-gold/30 max-w-2xl w-full p-8 relative shadow-2xl my-auto">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-white hover:text-luxury-gold" data-testid="admin-editor-close">
              <X size={20} />
            </button>
            <span className="text-[9px] uppercase tracking-[0.25em] text-luxury-gold font-semibold block mb-1">{isNew ? 'Create Lot' : 'Edit Lot'}</span>
            <h3 className="text-xl font-serif-luxury text-white mb-1">{isNew ? 'Add New Auction Lot' : editing.title}</h3>
            <div className="h-0.5 w-12 bg-luxury-gold mb-6"></div>

            <PreviewCard art={editing} />

            <form onSubmit={saveArtifact} className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Title</label>
                <input required value={editing.title} onChange={(e) => handleField('title', e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-3.5 py-3 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-title" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Category</label>
                  <select value={editing.category} onChange={(e) => handleField('category', e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-3.5 py-3 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-category">
                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Status</label>
                  <select value={editing.status} onChange={(e) => handleField('status', e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-3.5 py-3 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-status">
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Year</label>
                  <input value={editing.year} onChange={(e) => handleField('year', e.target.value)} placeholder="1969" className="w-full bg-black border border-white/10 text-white text-xs px-3.5 py-3 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-year" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Auction / Sale Date</label>
                  <input value={editing.auction_date} onChange={(e) => handleField('auction_date', e.target.value)} placeholder="Oct 2025 / Sold in 2024" className="w-full bg-black border border-white/10 text-white text-xs px-3.5 py-3 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-date" />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Auction Photo</label>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); uploadImage(e.dataTransfer.files); }}
                  className="border border-dashed border-white/20 hover:border-luxury-gold/50 bg-black/30 p-5 text-center transition-colors duration-300 flex flex-col items-center gap-3"
                  data-testid="admin-upload-zone"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={24} className="text-luxury-gold animate-spin" />
                      <span className="text-[10px] font-serif-luxury uppercase tracking-widest text-luxury-gold">Uploading Photo…</span>
                    </>
                  ) : editing.image ? (
                    <img
                      src={editing.image}
                      alt="preview"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="w-28 h-28 object-cover border border-white/10 grayscale brightness-75"
                    />
                  ) : (
                    <>
                      <UploadCloud size={26} className="text-luxury-muted stroke-[1.2]" />
                      <span className="text-[10px] text-luxury-muted font-sans-luxury">Drag &amp; drop or choose an image from your computer (JPG, PNG, GIF, WEBP · max 10MB)</span>
                    </>
                  )}
                  <label className="cursor-pointer bg-white/5 border border-white/10 hover:border-luxury-gold text-white hover:text-luxury-gold px-4 py-2 text-[9px] uppercase tracking-widest font-sans-luxury transition-colors duration-300">
                    {editing.image ? 'Replace Photo' : 'Choose File'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadImage(e.target.files)}
                      data-testid="admin-upload-input"
                    />
                  </label>
                </div>
                <details className="mt-2">
                  <summary className="text-[9px] uppercase tracking-widest text-luxury-muted font-sans-luxury cursor-pointer hover:text-luxury-gold">or paste an image URL</summary>
                  <input
                    value={editing.image}
                    onChange={(e) => handleField('image', e.target.value)}
                    placeholder="https://..."
                    className="w-full bg-black border border-white/10 text-white text-xs px-3.5 py-3 focus:border-luxury-gold outline-none rounded-none mt-2"
                    data-testid="admin-field-image"
                  />
                </details>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Result / Estimated Value</label>
                <input value={editing.estimated_value} onChange={(e) => handleField('estimated_value', e.target.value)} placeholder="Sold for $410,000  /  $200,000 - $300,000" className="w-full bg-black border border-white/10 text-white text-xs px-3.5 py-3 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-value" />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-1.5">Description</label>
                <textarea rows={3} value={editing.description} onChange={(e) => handleField('description', e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-3.5 py-3 focus:border-luxury-gold outline-none rounded-none resize-none" data-testid="admin-field-description" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-luxury-muted font-sans-luxury mb-1.5">Starting Bid</label>
                  <input type="number" value={editing.starting_bid} onChange={(e) => handleField('starting_bid', e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-3 py-2.5 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-starting-bid" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-luxury-muted font-sans-luxury mb-1.5">Current Bid</label>
                  <input type="number" value={editing.current_bid} onChange={(e) => handleField('current_bid', e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-3 py-2.5 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-current-bid" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-luxury-muted font-sans-luxury mb-1.5">Bids Count</label>
                  <input type="number" value={editing.bids_count} onChange={(e) => handleField('bids_count', e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-3 py-2.5 focus:border-luxury-gold outline-none rounded-none" data-testid="admin-field-bids-count" />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setEditing(null)} className="w-1/3 border border-white/20 hover:border-white text-white font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold py-3.5 transition-colors duration-300">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-gold w-2/3 font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold py-3.5 rounded-none flex items-center justify-center gap-2" data-testid="admin-save-lot">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                  {isNew ? 'Create Lot' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
